import { DateTime, dt } from "../../../../lib/datetime";
import { TaxPayment, TaxTypeNode, TaxYearNode } from "../types";

export function paidAmount({ amount, flow }: TaxPayment): number {
    return (flow === 'outflow' ? amount : -amount) / 100;
}

export function totalOf(payments: TaxPayment[]): number {
    return payments.reduce((total, payment) => total + paidAmount(payment), 0);
}

export function buildTaxTree(payments: TaxPayment[]): TaxYearNode[] {
    const years = [...new Set(payments.map((payment) => payment.year))].sort((left, right) => right - left);

    return years.map((year) => {
        const yearPayments = payments.filter((payment) => payment.year === year);
        const key = `year:${year}`;

        return {
            key,
            year,
            label: `Belastingjaar ${year}`,
            taxTypes: buildTaxTypeNodes(yearPayments, key),
            total: totalOf(yearPayments),
        };
    });
}

function buildTaxTypeNodes(payments: TaxPayment[], yearKey: string): TaxTypeNode[] {
    const byGroup = new Map<string, TaxPayment[]>();

    for (const payment of payments) {
        const group = `${payment.tax_group}:${payment.tax_group_label}`;
        byGroup.set(group, [...(byGroup.get(group) ?? []), payment]);
    }

    return [...byGroup.entries()]
        .map(([group, ofGroup]) => ({
            key: `${yearKey}|type:${group}`,
            label: ofGroup[0].tax_group_label,
            payments: [...ofGroup].sort(comparePaidOnDescending),
            total: totalOf(ofGroup),
        }))
        .sort((left, right) => right.total - left.total);
}

function comparePaidOnDescending(left: TaxPayment, right: TaxPayment): number {
    return (right.paid_on ?? '').localeCompare(left.paid_on ?? '');
}

export function formatPaidOn(paidOn: string | null): string {
    if (!paidOn) return '—';

    return DateTime.isValid(paidOn) ? dt(paidOn).format('DD-MM-YYYY') : paidOn;
}

export function paymentCountLabel(count: number): string {
    return `${count} ${count === 1 ? 'betaling' : 'betalingen'}`;
}

export function hasVisiblePayments(years: TaxYearNode[], isExpanded: (key: string) => boolean): boolean {
    return years.some((year) => isExpanded(year.key) && year.taxTypes.some((taxType) => isExpanded(taxType.key)));
}
